import { ReactElement, useCallback } from "react";

import {
  GcsImportIntegration,
  CampaignContactGcsImportArgs,
  ImportCampaignContactsFromGcsMutation,
  ImportCampaignContactsFromGcsMutationVariables,
} from "~frontend/__generated__/graphql";
import { GCS_CAMPAIGN_CONTACT_IMPORT } from "~frontend/graphql/campaigns";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../types";
import ImportForm from "./ImportForm";

export default function GCSImport({
  campaign,
  availableGCSIntegrations,
  readOnly,
  previousJobArgs,
  displayErrorToast,
  resetContactSourceSettings,
}: {
  campaign: CampaignForEditor;
  availableGCSIntegrations: GcsImportIntegration[];
  readOnly?: boolean;
  previousJobArgs?: CampaignContactGcsImportArgs;
  displayErrorToast: (message: string, errors: unknown) => void;
  resetContactSourceSettings: (() => void) | null;
}): ReactElement {
  const ngpVanCampaignIntegrationEnabled = !!(
    campaign.integration && campaign.integration.service == "NGPVAN"
  );

  const [startImport] = useMutation<
    ImportCampaignContactsFromGcsMutation,
    ImportCampaignContactsFromGcsMutationVariables
  >(GCS_CAMPAIGN_CONTACT_IMPORT);

  const startGCSImport = useCallback(
    async ({
      integrationId,
      objectName,
      phoneNumberColumn,
      vanIdColumn,
    }: {
      integrationId: string;
      objectName: string;
      phoneNumberColumn: string;
      vanIdColumn?: string;
    }) => {
      const { errors } = await startImport({
        variables: {
          input: {
            campaignId: campaign.id,
            integrationId,
            objectName,
            phoneNumberColumn,
            vanIdColumn:
              vanIdColumn && ngpVanCampaignIntegrationEnabled
                ? vanIdColumn
                : null,
          },
        },
      });

      if (errors) {
        displayErrorToast("Failed to start GCS import", errors);
      }
    },
    [campaign, startImport, displayErrorToast, ngpVanCampaignIntegrationEnabled]
  );

  return (
    <ImportForm
      onSubmit={readOnly ? undefined : startGCSImport}
      availableGCSIntegrations={availableGCSIntegrations}
      initialValues={{
        campaignId: campaign.id,
        integrationId:
          previousJobArgs?.integrationId ?? availableGCSIntegrations[0]?.id,
        objectName: previousJobArgs?.objectName ?? "",
        phoneNumberColumn: previousJobArgs?.phoneNumberColumn ?? "",
        vanIdColumn: previousJobArgs?.vanIdColumn ?? "",
      }}
      readOnly={readOnly}
      resetContactSourceSettings={resetContactSourceSettings}
      ngpVanCampaignIntegrationEnabled={ngpVanCampaignIntegrationEnabled}
    />
  );
}
